import { makeAutoObservable } from "mobx";

export type FocusRect = {
  // Normalized source-space rect in [0..1]
  x: number;
  y: number;
  w: number;
  h: number;
};

export type FocusSummary = {
  rect: FocusRect;
  confidence: number;
  analyzedUntilSec: number;
  provenance?: "client" | "server";
  updatedAtUnixSec?: number;
};

export type FocusAnalysisStatus = "processing" | "partial" | "ready" | "error";

export type FocusAnalysisDoc = {
  version: number;
  status: FocusAnalysisStatus;
  publishedChunkCount: number;
  chunkCount?: number;
  chunk0DurationSec?: number;
  chunkDurationSec?: number;
  publishedUntilSec?: number;
  totalSec?: number;
  quickSummary?: FocusSummary;
};

export type FocusKeyframe = {
  tSec: number;
  rect: FocusRect;
  confidence: number;
  numFaces?: number;
  face?: FocusFaceMeta | null;
};

export type FocusFaceMeta = {
  faceRect?: FocusRect;
  faceArea?: number;
  detectionConfidence?: number;
  landmarkingConfidence?: number;
  panAngle?: number;
  tiltAngle?: number;
  rollAngle?: number;
  blurred?: unknown;
  underExposed?: unknown;
  joy?: unknown;
  sorrow?: unknown;
  anger?: unknown;
  surprise?: unknown;
  headwear?: unknown;
};

export type FocusChunkFile = {
  trackId: string;
  t0Sec: number;
  t1Sec: number;
  keyframes: FocusKeyframe[];
};

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

const normalizeRect = (rect: FocusRect): FocusRect => {
  const x = clamp01(Number(rect?.x ?? 0));
  const y = clamp01(Number(rect?.y ?? 0));
  const wRaw = clamp01(Number(rect?.w ?? 0));
  const hRaw = clamp01(Number(rect?.h ?? 0));
  const w = Math.max(0, Math.min(1 - x, wRaw));
  const h = Math.max(0, Math.min(1 - y, hRaw));
  return { x, y, w, h };
};

const normalizeSummary = (summary: FocusSummary): FocusSummary => {
  const rect = normalizeRect(summary.rect);
  const confidence = Number(summary.confidence ?? 0);
  const analyzedUntilSec = Math.max(0, Number(summary.analyzedUntilSec ?? 0));
  return {
    rect,
    confidence: Number.isFinite(confidence) ? confidence : 0,
    analyzedUntilSec,
    provenance: summary.provenance,
    updatedAtUnixSec: summary.updatedAtUnixSec,
  };
};

const normalizeKeyframe = (kf: any): FocusKeyframe | null => {
  if (!kf) return null;
  const tSec = Number(kf.tSec ?? kf.t ?? kf.timeSec ?? NaN);
  if (!Number.isFinite(tSec)) return null;
  const rectRaw = kf.rect ?? kf.focusRect ?? kf.r ?? null;
  if (!rectRaw) return null;
  const rect = normalizeRect({
    x: Number(rectRaw.x ?? 0),
    y: Number(rectRaw.y ?? 0),
    w: Number(rectRaw.w ?? rectRaw.width ?? 0),
    h: Number(rectRaw.h ?? rectRaw.height ?? 0),
  });
  const confidence = Number(kf.confidence ?? kf.c ?? 0);

  const numFacesRaw = Number(kf.numFaces ?? kf.faces ?? kf.faceCount ?? NaN);
  const numFaces = Number.isFinite(numFacesRaw) ? Math.max(0, Math.round(numFacesRaw)) : undefined;

  const faceRaw = kf.face ?? kf.focusFaceMeta ?? kf.faceMeta ?? null;
  let face: FocusFaceMeta | null | undefined = undefined;
  try {
    if (faceRaw && typeof faceRaw === "object") {
      const faceRectRaw = (faceRaw as any).faceRect ?? (faceRaw as any).rect ?? null;
      const faceRect = faceRectRaw
        ? normalizeRect({
            x: Number((faceRectRaw as any).x ?? 0),
            y: Number((faceRectRaw as any).y ?? 0),
            w: Number((faceRectRaw as any).w ?? (faceRectRaw as any).width ?? 0),
            h: Number((faceRectRaw as any).h ?? (faceRectRaw as any).height ?? 0),
          })
        : undefined;

      const faceArea = Number((faceRaw as any).faceArea ?? NaN);
      const detectionConfidence = Number((faceRaw as any).detectionConfidence ?? NaN);
      const landmarkingConfidence = Number((faceRaw as any).landmarkingConfidence ?? NaN);
      const panAngle = Number((faceRaw as any).panAngle ?? NaN);
      const tiltAngle = Number((faceRaw as any).tiltAngle ?? NaN);
      const rollAngle = Number((faceRaw as any).rollAngle ?? NaN);

      face = {
        faceRect,
        faceArea: Number.isFinite(faceArea) ? faceArea : undefined,
        detectionConfidence: Number.isFinite(detectionConfidence) ? detectionConfidence : undefined,
        landmarkingConfidence: Number.isFinite(landmarkingConfidence) ? landmarkingConfidence : undefined,
        panAngle: Number.isFinite(panAngle) ? panAngle : undefined,
        tiltAngle: Number.isFinite(tiltAngle) ? tiltAngle : undefined,
        rollAngle: Number.isFinite(rollAngle) ? rollAngle : undefined,
        blurred: (faceRaw as any).blurred,
        underExposed: (faceRaw as any).underExposed,
        joy: (faceRaw as any).joy,
        sorrow: (faceRaw as any).sorrow,
        anger: (faceRaw as any).anger,
        surprise: (faceRaw as any).surprise,
        headwear: (faceRaw as any).headwear,
      };
    }
  } catch {
    face = undefined;
  }
  return {
    tSec,
    rect,
    confidence: Number.isFinite(confidence) ? confidence : 0,
    numFaces,
    face: face === undefined ? undefined : face,
  };
};

class FocusStoreImpl {
  byTrackId = new Map<string, FocusSummary>();
  analysisByTrackId = new Map<string, FocusAnalysisDoc>();
  chunksByTrackId = new Map<string, Map<number, FocusChunkFile>>();
  version = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setQuickSummary(params: { trackId: string; summary: FocusSummary }) {
    const trackId = String(params.trackId || "");
    if (!trackId) return;

    this.byTrackId.set(trackId, normalizeSummary(params.summary));
    this.version++;
  }

  /**
   * Update analysis metadata from Firestore doc tracks/{trackId}/analysis/focus.
   * Does NOT fetch chunks; it only stores metadata and quickSummary (if present).
   */
  setAnalysisDocFromFirestore(params: { trackId: string; data: any }) {
    const trackId = String(params.trackId || "");
    if (!trackId) return;

    const d = params.data ?? {};
    const version = Number(d.version ?? 1);
    const statusRaw = String(d.status ?? "processing").toLowerCase();
    const status: FocusAnalysisStatus =
      statusRaw === "ready" ? "ready" :
      statusRaw === "partial" ? "partial" :
      statusRaw === "error" ? "error" :
      "processing";

    const publishedChunkCount = Math.max(0, Number(d.publishedChunkCount ?? d.publishedChunks ?? 0) || 0);

    const chunkCountNum = Number(d.chunkCount ?? NaN);
    const chunk0DurationSecNum = Number(d.chunk0DurationSec ?? NaN);
    const chunkDurationSecNum = Number(d.chunkDurationSec ?? NaN);
    const publishedUntilSecNum = Number(d.publishedUntilSec ?? NaN);
    const totalSecNum = Number(d.totalSec ?? d.totalDurationSec ?? NaN);

    const quick = d.quickSummary ?? null;
    let quickSummary: FocusSummary | undefined = undefined;
    try {
      if (quick?.rect) {
        quickSummary = normalizeSummary({
          rect: {
            x: Number(quick.rect.x ?? 0),
            y: Number(quick.rect.y ?? 0),
            w: Number(quick.rect.w ?? 0),
            h: Number(quick.rect.h ?? 0),
          },
          confidence: Number(quick.confidence ?? 0),
          analyzedUntilSec: Number(quick.analyzedUntilSec ?? 0),
          provenance: typeof quick.provenance === "string" ? quick.provenance : undefined,
          updatedAtUnixSec: Number(quick.updatedAtUnixSec ?? 0) || undefined,
        });
      }
    } catch {
      quickSummary = undefined;
    }

    this.analysisByTrackId.set(trackId, {
      version: Number.isFinite(version) ? version : 1,
      status,
      publishedChunkCount,
      chunkCount: Number.isFinite(chunkCountNum) ? chunkCountNum : undefined,
      chunk0DurationSec: Number.isFinite(chunk0DurationSecNum) ? chunk0DurationSecNum : undefined,
      chunkDurationSec: Number.isFinite(chunkDurationSecNum) ? chunkDurationSecNum : undefined,
      publishedUntilSec: Number.isFinite(publishedUntilSecNum) ? publishedUntilSecNum : undefined,
      totalSec: Number.isFinite(totalSecNum) ? totalSecNum : undefined,
      quickSummary,
    });

    if (quickSummary) {
      this.byTrackId.set(trackId, quickSummary);
    }

    // On error, if we have no focus data at all, ensure a safe default.
    if (status === "error" && !this.byTrackId.has(trackId)) {
      this.byTrackId.set(trackId, {
        rect: { x: 0, y: 0, w: 1, h: 1 },
        confidence: 0,
        analyzedUntilSec: 0,
      });
    }

    this.version++;
  }

  setChunk(params: { trackId: string; chunkIndex: number; chunk: FocusChunkFile }) {
    const trackId = String(params.trackId || "");
    if (!trackId) return;
    const chunkIndex = Number(params.chunkIndex);
    if (!Number.isFinite(chunkIndex) || chunkIndex < 0) return;

    const c = params.chunk ?? null;
    if (!c) return;

    const t0Sec = Number((c as any).t0Sec ?? (c as any).t0 ?? 0);
    const t1Sec = Number((c as any).t1Sec ?? (c as any).t1 ?? 0);
    const keyframesRaw = Array.isArray((c as any).keyframes) ? (c as any).keyframes : [];
    const keyframes = keyframesRaw.map(normalizeKeyframe).filter(Boolean) as FocusKeyframe[];
    keyframes.sort((a, b) => a.tSec - b.tSec);

    let byChunk = this.chunksByTrackId.get(trackId);
    if (!byChunk) {
      byChunk = new Map<number, FocusChunkFile>();
      this.chunksByTrackId.set(trackId, byChunk);
    }

    byChunk.set(chunkIndex, {
      trackId: String((c as any).trackId ?? trackId),
      t0Sec: Number.isFinite(t0Sec) ? t0Sec : 0,
      t1Sec: Number.isFinite(t1Sec) ? t1Sec : 0,
      keyframes,
    });

    this.version++;
  }

  hasChunk(trackId: string | null | undefined, chunkIndex: number): boolean {
    const id = String(trackId || "");
    if (!id) return false;
    const idx = Number(chunkIndex);
    if (!Number.isFinite(idx) || idx < 0) return false;
    const m = this.chunksByTrackId.get(id);
    return !!m?.has(idx);
  }

  getChunk(trackId: string | null | undefined, chunkIndex: number): FocusChunkFile | null {
    const id = String(trackId || "");
    if (!id) return null;
    const idx = Number(chunkIndex);
    if (!Number.isFinite(idx) || idx < 0) return null;
    return this.chunksByTrackId.get(id)?.get(idx) ?? null;
  }

  /**
   * Best-effort lookup of focus rect at time.
   * For now: step-function within loaded chunks; fallback to quickSummary.
   */
  getRectAtTimeSec(trackId: string | null | undefined, tSec: number): FocusRect | null {
    const id = String(trackId || "");
    if (!id) return null;
    const t = Number(tSec);
    if (!Number.isFinite(t) || t < 0) return this.byTrackId.get(id)?.rect ?? null;

    const byChunk = this.chunksByTrackId.get(id);
    if (byChunk && byChunk.size > 0) {
      // Find any chunk whose range includes t.
      for (const chunk of byChunk.values()) {
        const t0 = Number(chunk.t0Sec ?? 0);
        const t1 = Number(chunk.t1Sec ?? 0);
        if (Number.isFinite(t0) && Number.isFinite(t1) && t >= t0 && t <= t1 && chunk.keyframes.length > 0) {
          // Find last keyframe <= t (step).
          let last: FocusKeyframe | null = null;
          for (const kf of chunk.keyframes) {
            if (kf.tSec <= t) last = kf;
            else break;
          }
          if (last) return last.rect;
          return chunk.keyframes[0].rect;
        }
      }
    }

    return this.byTrackId.get(id)?.rect ?? null;
  }

  /**
   * Best-effort lookup of the full keyframe at time (rect + metadata).
   * For now: step-function within loaded chunks; returns the nearest <= t keyframe in the containing chunk.
   */
  getKeyframeAtTimeSec(trackId: string | null | undefined, tSec: number): FocusKeyframe | null {
    const id = String(trackId || "");
    if (!id) return null;
    const t = Number(tSec);
    if (!Number.isFinite(t) || t < 0) return null;

    const byChunk = this.chunksByTrackId.get(id);
    if (byChunk && byChunk.size > 0) {
      for (const chunk of byChunk.values()) {
        const t0 = Number(chunk.t0Sec ?? 0);
        const t1 = Number(chunk.t1Sec ?? 0);
        if (Number.isFinite(t0) && Number.isFinite(t1) && t >= t0 && t <= t1 && chunk.keyframes.length > 0) {
          let last: FocusKeyframe | null = null;
          for (const kf of chunk.keyframes) {
            if (kf.tSec <= t) last = kf;
            else break;
          }
          return last ?? chunk.keyframes[0] ?? null;
        }
      }
    }

    return null;
  }

  /**
   * Heuristic: returns the median number of faces from the loaded keyframes (chunk0 if present),
   * or null if we have no face counts yet.
   */
  getMedianNumFaces(trackId: string | null | undefined): number | null {
    const id = String(trackId || "");
    if (!id) return null;
    const byChunk = this.chunksByTrackId.get(id);
    if (!byChunk || byChunk.size === 0) return null;

    const chunk0 = byChunk.get(0) ?? null;
    const keyframes = chunk0?.keyframes?.length
      ? chunk0.keyframes
      : Array.from(byChunk.values())[0]?.keyframes ?? [];

    const faces: number[] = [];
    for (const kf of keyframes) {
      const n = Number((kf as any).numFaces);
      if (Number.isFinite(n)) faces.push(Math.max(0, Math.round(n)));
    }
    if (faces.length === 0) return null;
    faces.sort((a, b) => a - b);
    return faces[Math.floor(faces.length / 2)] ?? null;
  }

  getQuickSummary(trackId: string | null | undefined): FocusSummary | null {
    const id = String(trackId || "");
    if (!id) return null;
    return this.byTrackId.get(id) ?? null;
  }

  getAnalysisDoc(trackId: string | null | undefined): FocusAnalysisDoc | null {
    const id = String(trackId || "");
    if (!id) return null;
    return this.analysisByTrackId.get(id) ?? null;
  }

  clear(trackId: string | null | undefined) {
    const id = String(trackId || "");
    if (!id) return;
    const had =
      this.byTrackId.delete(id) ||
      this.analysisByTrackId.delete(id) ||
      this.chunksByTrackId.delete(id);
    if (!had) return;
    this.version++;
  }

  clearAll() {
    if (this.byTrackId.size === 0 && this.analysisByTrackId.size === 0 && this.chunksByTrackId.size === 0) return;
    this.byTrackId.clear();
    this.analysisByTrackId.clear();
    this.chunksByTrackId.clear();
    this.version++;
  }
}

export const FocusStore = new FocusStoreImpl();
